"use client"

import React, { useState, useEffect, useId } from "react"

/* ── Hooks ported from terra-one ── */

function useWindowSize() {
  const [size, setSize] = useState<"mobile" | "tablet" | "desktop" | null>(null)

  useEffect(() => {
    if (typeof window === "undefined") return
    const check = () => {
      if (window.innerWidth > 960) setSize("desktop")
      else if (window.innerWidth > 600) setSize("tablet")
      else setSize("mobile")
    }
    window.addEventListener("resize", check)
    check()
    return () => window.removeEventListener("resize", check)
  }, [])

  return { size }
}

function useMouse() {
  const [mouse, setMouse] = useState(false)

  useEffect(() => {
    if (typeof document === "undefined") return
    const onMouseDown = () => setMouse(true)
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.keyCode === 9) setMouse(false)
    }
    document.body.addEventListener("mousedown", onMouseDown)
    document.body.addEventListener("keydown", onKeyDown)
    return () => {
      document.body.removeEventListener("mousedown", onMouseDown)
      document.body.removeEventListener("keydown", onKeyDown)
    }
  }, [])

  return { mouse }
}

function formatSpecialCharText(text: string) {
  if (!text || typeof text !== "string") return null
  const formatted = text
    .split(/(™)|(©)|(®)/g)
    .map(ch => {
      if (ch === "™") return "<sup>&trade;</sup>"
      if (ch === "©") return "<sup>&copy;</sup>"
      if (ch === "®") return "<sup>&reg;</sup>"
      return ch ?? ""
    })
    .join("")
  return { __html: formatted }
}

function jumpToContent(contentID: string) {
  if (typeof window === "undefined" || typeof document === "undefined") return
  if (window.innerWidth > 900) return

  const el = document.getElementById(contentID)
  if (!el) return

  document.documentElement.style.scrollBehavior = "smooth"
  el.scrollIntoView()
  document.documentElement.style.scrollBehavior = ""
}

const ARROW_SIZE = 20

/* ── Desktop arrow (text-entries, active tab) ── */

function DesktopArrow({
  orientation,
}: {
  orientation: "top" | "bottom" | "left" | "right"
}) {
  const pos: Record<string, React.CSSProperties> = {
    top: { bottom: "-22px", left: 0 },
    bottom: { top: "-22px", left: 0 },
    left: { right: "-22px", top: "50%", transform: "translateY(-50%)" },
    right: { left: "-22px", top: "50%", transform: "translateY(-50%)" },
  }

  const border: Record<string, React.CSSProperties> = {
    top: {
      borderLeft: `${ARROW_SIZE}px solid transparent`,
      borderRight: `${ARROW_SIZE}px solid transparent`,
      borderTop: `${ARROW_SIZE}px solid var(--color-blue-dnavy)`,
    },
    bottom: {
      borderLeft: `${ARROW_SIZE}px solid transparent`,
      borderRight: `${ARROW_SIZE}px solid transparent`,
      borderBottom: `${ARROW_SIZE}px solid var(--color-blue-dnavy)`,
    },
    left: {
      borderTop: `${ARROW_SIZE}px solid transparent`,
      borderBottom: `${ARROW_SIZE}px solid transparent`,
      borderLeft: `${ARROW_SIZE}px solid var(--color-blue-dnavy)`,
    },
    right: {
      borderTop: `${ARROW_SIZE}px solid transparent`,
      borderBottom: `${ARROW_SIZE}px solid transparent`,
      borderRight: `${ARROW_SIZE}px solid var(--color-blue-dnavy)`,
    },
  }

  return (
    <span
      className="absolute block w-0 h-0 max-terra:hidden"
      style={{ ...pos[orientation], ...border[orientation] }}
    />
  )
}

/* ── Thumbnail arrow (round/square, active tab) ── */

function ThumbnailArrow({
  orientation,
}: {
  orientation: "top" | "bottom" | "left" | "right"
}) {
  const pos: Record<string, React.CSSProperties> = {
    top: { bottom: "-22px", left: `calc(50% - ${ARROW_SIZE}px)` },
    bottom: { top: "-22px", left: `calc(50% - ${ARROW_SIZE}px)` },
    left: { right: "-22px", top: `calc(50% - ${ARROW_SIZE}px)` },
    right: { left: "-22px", top: `calc(50% - ${ARROW_SIZE}px)` },
  }

  const border: Record<string, React.CSSProperties> = {
    top: {
      borderLeft: `${ARROW_SIZE}px solid transparent`,
      borderRight: `${ARROW_SIZE}px solid transparent`,
      borderTop: `${ARROW_SIZE}px solid var(--color-blue-trmb)`,
    },
    bottom: {
      borderLeft: `${ARROW_SIZE}px solid transparent`,
      borderRight: `${ARROW_SIZE}px solid transparent`,
      borderBottom: `${ARROW_SIZE}px solid var(--color-blue-trmb)`,
    },
    left: {
      borderTop: `${ARROW_SIZE}px solid transparent`,
      borderBottom: `${ARROW_SIZE}px solid transparent`,
      borderLeft: `${ARROW_SIZE}px solid var(--color-blue-trmb)`,
    },
    right: {
      borderTop: `${ARROW_SIZE}px solid transparent`,
      borderBottom: `${ARROW_SIZE}px solid transparent`,
      borderRight: `${ARROW_SIZE}px solid var(--color-blue-trmb)`,
    },
  }

  return (
    <span
      className="absolute block w-0 h-0 max-terra:hidden"
      style={{ ...pos[orientation], ...border[orientation] }}
    />
  )
}

/* ── Mobile active tab heading (hidden on desktop via terra: breakpoint) ── */

function MobileTopNavEntry({
  tab,
  contentfulId,
}: {
  tab: TabItem | undefined
  contentfulId?: string
}) {
  return (
    <div className="terra:hidden">
      <div
        className="pb-4 border-b-[3px] border-solid border-blue-dnavy"
        data-sb-object-id={contentfulId || undefined}
      >
        {tab?.header && (
          <div
            className="mx-6 text-[16px] leading-7 font-sans"
            data-sb-field-path="header"
          >
            {tab.header}
          </div>
        )}
      </div>
      <div
        className="mx-[30px] mb-4 w-0 h-0"
        style={{
          borderLeft: "10px solid transparent",
          borderRight: "10px solid transparent",
          borderTop: "10px solid var(--color-blue-dnavy)",
        }}
      />
    </div>
  )
}

/* ── NavEntry ── */

interface NavEntryProps {
  index: number
  isActive: boolean
  setIndex: (i: number) => void
  header?: string
  description?: string
  image?: { url?: string; alt?: string }
  displayThumbnails?: boolean
  contentID: string
  numberOfTabs: number
  contentfulId?: string
  orientation: "top" | "bottom" | "left" | "right"
  isLight: boolean
  isDark: boolean
  isThumbnail: boolean
  isThumbnailRound: boolean
  shouldHide: boolean
}

function NavEntry({
  index,
  isActive,
  setIndex,
  header,
  description,
  image,
  displayThumbnails,
  contentID,
  numberOfTabs,
  contentfulId,
  orientation,
  isLight,
  isDark,
  isThumbnail,
  isThumbnailRound,
  shouldHide,
}: NavEntryProps) {
  const { mouse } = useMouse()

  const isTop = orientation === "top"
  const isBottom = orientation === "bottom"
  const isLeft = orientation === "left"
  const isRight = orientation === "right"
  const isVertical = isLeft || isRight
  const isTextEntries = isLight || isDark

  const tabsNumberForAria =
    displayThumbnails && numberOfTabs > 4 ? "4" : String(numberOfTabs)

  const widthClass = isVertical
    ? "terra:w-[260px]"
    : numberOfTabs >= 4
      ? "terra:w-1/4"
      : numberOfTabs === 3
        ? "terra:w-1/3"
        : numberOfTabs === 2
          ? "terra:w-1/2"
          : "terra:w-full"

  const buttonClasses = [
    "relative flex items-center cursor-pointer outline-0 border-0 border-solid",
    shouldHide ? "terra:hidden" : "",
    !mouse ? "focus:outline-2 focus:outline-blue-trmb" : "",

    // ── Text entries: desktop ──
    isTextEntries
      ? [
          "terra:justify-center terra:px-[30px] terra:py-5",
          widthClass,

          // Left/right: narrower padding, start-aligned
          isVertical
            ? "terra:!justify-start terra:!text-left terra:!px-[10px] terra:!py-[10px]"
            : "",

          // Light borders per side
          isLight && isTop
            ? `terra:border-b-[6px] ${isActive ? "terra:border-b-blue-dnavy" : "terra:border-b-gray-m2"}`
            : "",
          isLight && isBottom
            ? `terra:border-t-[6px] ${isActive ? "terra:border-t-blue-dnavy" : "terra:border-t-gray-m2"}`
            : "",
          isLight && isLeft
            ? `terra:border-r-[6px] ${isActive ? "terra:border-r-blue-dnavy" : "terra:border-r-gray-m2"}`
            : "",
          isLight && isRight
            ? `terra:border-l-[6px] ${isActive ? "terra:border-l-blue-dnavy" : "terra:border-l-gray-m2"} terra:-translate-x-[6px]`
            : "",

          // Dark variant
          isDark
            ? isActive
              ? "terra:border-blue-dnavy terra:text-white terra:bg-blue-dnavy"
              : "terra:border-gray-m2 terra:text-blue-dnavy terra:bg-gray-m2"
            : "",
          isDark && (isTop || isBottom) ? "terra:!w-[calc(25%-2px)]" : "",
          isDark && isVertical ? "terra:!text-left terra:mb-1" : "",
        ].join(" ")
      : "",

    // ── Thumbnails: desktop ──
    isThumbnail
      ? [
          "terra:justify-center terra:items-center",
          isThumbnailRound
            ? `terra:px-[30px] terra:py-0 ${widthClass}`
            : "terra:w-[126px] terra:max-w-[126px] terra:h-[126px] terra:max-h-[126px]",
          (isTop || isBottom) && !isThumbnailRound && index < numberOfTabs - 1
            ? "terra:mr-5"
            : "",
          isVertical && !isThumbnailRound && index < numberOfTabs - 1
            ? "terra:mb-5"
            : "",
          isThumbnailRound && isVertical && index < numberOfTabs - 1
            ? "terra:mb-[30px]"
            : "",
        ].join(" ")
      : "",

    // ── Mobile (≤960px): text entries ──
    isTextEntries
      ? [
          "max-terra:w-full max-terra:border-0 max-terra:px-5 max-terra:py-[15px]",
          "max-terra:justify-start max-terra:translate-x-0",
          isActive ? "max-terra:bg-blue-dnavy" : "max-terra:bg-gray-m2",
          index < numberOfTabs - 1 ? "max-terra:mb-[10px]" : "",
        ].join(" ")
      : "",

    // ── Mobile (≤960px): thumbnails ──
    isThumbnail
      ? [
          "max-terra:justify-center max-terra:items-center",
          isThumbnailRound
            ? "max-terra:px-[10px] max-terra:py-0"
            : "max-terra:p-[5px]",
        ].join(" ")
      : "",
  ]
    .filter(Boolean)
    .join(" ")

  // Nav-text styles
  const navTextClasses = [
    "pointer-events-none",
    isLight
      ? `font-bold ${isActive ? "terra:text-blue-dnavy max-terra:text-white" : "text-blue-trmb"}`
      : "",
    isThumbnail ? "hidden" : "",
  ]
    .filter(Boolean)
    .join(" ")

  // Nav-title styles
  const navTitleClasses = [
    "font-bold m-0 text-[16px] leading-[20px] font-sans relative",
    isTextEntries ? "uppercase max-terra:normal-case max-terra:text-left" : "",
  ]
    .filter(Boolean)
    .join(" ")

  // Subtitle: hidden for light, visible for dark
  const showSubtitle = isDark && description

  return (
    <button
      onClick={() => {
        setIndex(index)
        jumpToContent(contentID)
      }}
      className={buttonClasses}
      aria-selected={isActive}
      aria-label={
        isActive
          ? `Selected Tab ${index + 1} of ${tabsNumberForAria}`
          : `Unselected Tab ${index + 1} of ${tabsNumberForAria}`
      }
      aria-controls={`Selected tab ${index + 1} of ${tabsNumberForAria}: ${header}`}
      tabIndex={mouse ? -1 : 0}
      role="tab"
      data-sb-object-id={contentfulId || undefined}
    >
      {/* Thumbnail */}
      {displayThumbnails && (
        <div
          className={[
            "relative overflow-hidden",
            isThumbnailRound
              ? [
                  "flex items-center rounded-full",
                  "terra:w-[200px] terra:max-w-[200px] terra:h-[200px] terra:max-h-[200px] terra:p-[10px]",
                  "max-terra:w-[112px] max-terra:h-[112px] max-terra:p-[5px]",
                  isActive
                    ? "opacity-100 border-[5px] border-solid border-blue-trmb"
                    : "opacity-50",
                ].join(" ")
              : [
                  "w-full h-full p-[5px]",
                  isActive
                    ? "opacity-100 border-[5px] border-solid border-blue-trmb"
                    : "opacity-50",
                ].join(" "),
            isTextEntries ? "max-terra:hidden" : "",
            !isThumbnailRound
              ? "min-w-[70px] w-[70px] h-[70px] mr-4 rounded-full"
              : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <img
            src={image?.url || ""}
            alt={image?.alt || "Trimble Logo"}
            className={[
              "w-full h-full object-cover",
              isThumbnailRound ? "rounded-full object-center" : "",
            ].join(" ")}
            data-sb-field-path="image"
          />
          {isActive && isThumbnail && (
            <ThumbnailArrow orientation={orientation} />
          )}
        </div>
      )}

      {/* Tab text */}
      <div className={navTextClasses}>
        {header && (
          <div className={navTitleClasses}>
            <span
              dangerouslySetInnerHTML={
                formatSpecialCharText(header) || undefined
              }
            />
          </div>
        )}
        {showSubtitle && (
          <div className="font-normal text-sm leading-[18px] mt-1.5 max-terra:text-left">
            {description}
          </div>
        )}
      </div>

      {/* Desktop arrow */}
      {isActive && isTextEntries && <DesktopArrow orientation={orientation} />}
    </button>
  )
}

/* ── TabContent ── */

function TabContent({
  isActive,
  children,
  contentfulId,
}: {
  isActive: boolean
  children: React.ReactNode
  contentfulId?: string
}) {
  return (
    <div
      className={`w-full ${isActive ? "block" : "hidden"}`}
      data-sb-object-id={contentfulId || undefined}
    >
      {children}
    </div>
  )
}

/* ── Main Tabs component ── */

export interface TabItem {
  header?: string
  description?: string
  image?: { url?: string; alt?: string }
  content?: React.ReactNode
  contentfulId?: string
}

export interface TabsProps {
  orientation?: string
  variant?: string
  displayThumbnails?: boolean
  tabs: TabItem[]
  paddingClass?: string
  index?: number
  contentfulId?: string
}

export default function Tabs({
  orientation,
  variant,
  displayThumbnails,
  tabs,
  paddingClass,
  index,
  contentfulId,
}: TabsProps) {
  const { size } = useWindowSize()
  const [activeIndex, setActiveIndex] = useState(0)
  const reactId = useId()

  const orient = (
    ["top", "bottom", "left", "right"].includes(orientation || "")
      ? orientation
      : "top"
  ) as "top" | "bottom" | "left" | "right"

  const isTop = orient === "top"
  const isBottom = orient === "bottom"
  const isLeft = orient === "left"
  const isRight = orient === "right"
  const isVertical = isLeft || isRight

  const isDark = variant === "text-background-dark"
  const isThumbnailRound = variant === "thumbnail-round"
  const isThumbnailSquare = variant === "thumbnail-square"
  const isThumbnail = isThumbnailRound || isThumbnailSquare
  const isLight = !isDark && !isThumbnail
  const isTextEntries = isLight || isDark
  const thumbnails = displayThumbnails || isThumbnail

  const renderTopTab = tabs.find((_tab, i) => i === activeIndex)
  const contentID = `content-entries-${reactId}-${index ?? 0}`

  const mobileThumbnailTop =
    (orient !== "bottom" && isThumbnailSquare) ||
    (orient !== "bottom" && isThumbnailRound)

  const shouldHideTab = (i: number) => {
    if (i < 4) return false
    if (isTextEntries && (isTop || isBottom)) return true
    if (isThumbnailRound) return true
    if (isThumbnailSquare && (isTop || isBottom)) return true
    return false
  }

  if (!tabs?.length) return null

  // Container classes
  const containerClasses = [
    "w-full flex",
    paddingClass || "",
    isTop ? "flex-col" : "",
    isBottom ? "flex-col-reverse max-terra:flex-col" : "",
    isLeft ? "flex-row max-terra:flex-wrap" : "",
    isRight ? "flex-row-reverse max-terra:flex-row max-terra:flex-wrap" : "",
    isTextEntries ? "max-terra:flex-wrap" : "",
  ]
    .filter(Boolean)
    .join(" ")

  // Nav entries classes
  const navEntriesClasses = [
    "w-full flex",
    isVertical ? "terra:max-w-[260px] terra:flex-col" : "",

    // Light desktop borders
    isLight
      ? [
          isTop ? "terra:pb-6" : "",
          isBottom ? "terra:pt-6" : "",
          isLeft
            ? "terra:mr-[30px] terra:border-r-[6px] terra:border-solid terra:border-gray-m2"
            : "",
          isRight
            ? "terra:ml-[34px] terra:border-l-[6px] terra:border-solid terra:border-gray-m2"
            : "",
        ].join(" ")
      : "",

    // Dark desktop
    isDark
      ? [
          isTop || isBottom ? "terra:gap-0.5" : "",
          isVertical ? "terra:border-0" : "",
        ].join(" ")
      : "",

    // Thumbnails desktop
    isThumbnail
      ? [
          isThumbnailRound
            ? [
                isTop ? "terra:pb-6" : "",
                isBottom ? "terra:pt-6" : "",
                isLeft ? "terra:pr-[30px]" : "",
                isRight ? "terra:pl-[34px]" : "",
              ].join(" ")
            : [
                isTop || isBottom ? "terra:justify-center" : "",
                isLeft ? "terra:items-end" : "",
                isRight ? "terra:items-start" : "",
              ].join(" "),
          isVertical ? "terra:w-auto" : "",
        ].join(" ")
      : "",

    // Mobile: text entries overrides
    isTextEntries
      ? [
          "max-terra:w-full max-terra:max-w-none max-terra:flex-wrap",
          "max-terra:pt-6 max-terra:border-0 max-terra:m-0",
          "max-terra:order-2",
        ].join(" ")
      : "",

    // Mobile: thumbnails
    isThumbnail
      ? "max-terra:pb-6 max-terra:w-full max-terra:justify-center"
      : "",
  ]
    .filter(Boolean)
    .join(" ")

  return (
    <div
      className={containerClasses}
      data-sb-object-id={contentfulId || undefined}
    >
      {/* Mobile active tab heading */}
      <div id={contentID} className="max-terra:w-full scroll-mt-20">
        {!mobileThumbnailTop && (
          <MobileTopNavEntry tab={renderTopTab} contentfulId={contentfulId} />
        )}
      </div>

      {/* Tab buttons */}
      <div className={navEntriesClasses} role="tablist">
        {tabs.map((tab, i) => (
          <NavEntry
            key={`${contentID}-tab-${i}`}
            index={i}
            isActive={i === activeIndex}
            setIndex={setActiveIndex}
            header={tab.header}
            description={tab.description}
            image={tab.image}
            displayThumbnails={thumbnails}
            contentID={contentID}
            numberOfTabs={tabs.length}
            contentfulId={tab.contentfulId}
            orientation={orient}
            isLight={isLight}
            isDark={isDark}
            isThumbnail={isThumbnail}
            isThumbnailRound={isThumbnailRound}
            shouldHide={shouldHideTab(i)}
          />
        ))}
      </div>

      {/* Tab content panels */}
      <div
        className={`w-full max-w-full min-w-0 flex-1 ${isLeft ? "pl-10" : ""}`}
        id={size === "tablet" ? contentID : undefined}
        role="tabpanel"
        aria-labelledby={contentID}
      >
        {tabs.map((tab, i) => (
          <TabContent
            key={`${contentID}-content-${i}`}
            isActive={i === activeIndex}
            contentfulId={tab.contentfulId}
          >
            {tab.content}
          </TabContent>
        ))}
      </div>
    </div>
  )
}
